import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { UserDenormalizedWithoutLook } from "./user"
import { LiveFreeBoardId, LiveFreeBoardIdSchema, UserId, UserIdSchema } from "../id"
import { CreatedAt, UpdatedAt } from "./system"
import { validateUserFreeInputText } from "../user-free-input-text-constraint"
import { MediaImage } from "../common"

/**
 * フリーボードの種別。
 *
 * 現状は `image-and-text` の 1 種類のみ。1 つのフリーボードに text・image のいずれか、または両方を含められる。
 * 将来別種別 (例: 動画) を追加する余地を残すため、kind フィールド自体は維持する。
 */
export const LiveFreeBoardKind = z.literal("image-and-text")
export type LiveFreeBoardKind = z.infer<typeof LiveFreeBoardKind>

/**
 * フリーボードの公開状態。
 *
 * - `public`: 視聴者に表示される (デフォルト)
 * - `hidden`: 視聴者に表示されない (コンテンツはサーバ側に保持される)
 *
 * 将来 "follower-only" 等の特殊な公開状態を追加する余地を残すため、boolean ではなく文字列 enum とする。
 */
export const LiveFreeBoardVisibility = z.enum(["public", "hidden"])
export type LiveFreeBoardVisibility = z.infer<typeof LiveFreeBoardVisibility>

/**
 * フリーボードの表示位置
 * LIVE 表示エリアの幅・高さを 100 として、左上を原点 (0, 0)、右下を (100, 100) とするパーセンテージ。
 * 整数値のみ許容する。小数を扱わないことで、UI 上の表示・バリデーション・ログ閲覧をシンプルにする。
 */
export const LiveFreeBoardPosition = z.object({
  x: z.int().min(0).max(100).describe("LIVE 表示エリア基準の水平方向の位置 (%)"),
  y: z.int().min(0).max(100).describe("LIVE 表示エリア基準の垂直方向の位置 (%)"),
})
export type LiveFreeBoardPosition = z.infer<typeof LiveFreeBoardPosition>

/**
 * フリーボードに含まれるテキスト要素。
 * value は NG ワード検証や文字数制限を経たユーザー入力テキスト。
 */
export const LiveFreeBoardText = z.object({
  value: z.string(),
})
export type LiveFreeBoardText = z.infer<typeof LiveFreeBoardText>

/**
 * フリーボードに含まれる画像要素。
 * 画像本体は tmp から valid Storage パスへ移動された後の MediaImage (CDN variants 付き) で保持する。
 */
export const LiveFreeBoardImage = z.object({
  media: MediaImage.describe("CDN variants 付き画像。tmp からの移動完了後に書き込まれる"),
})
export type LiveFreeBoardImage = z.infer<typeof LiveFreeBoardImage>

export const LiveFreeBoardDocumentBody = z.object({
  kind: LiveFreeBoardKind,
  position: LiveFreeBoardPosition,
  text: LiveFreeBoardText.optional().describe("テキスト要素。存在しない場合は画像のみのフリーボード"),
  image: LiveFreeBoardImage.optional().describe("画像要素。存在しない場合はテキストのみのフリーボード"),
  visibility: LiveFreeBoardVisibility.optional().describe("公開状態。未設定は \"public\" と同等"),
  user: UserDenormalizedWithoutLook.describe("作成者の非正規化ユーザー情報"),
  last_updated_by_user_id: UserIdSchema.describe("最後に内容を更新したユーザーの ID。作成時は作成者 ID と同じ"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})
export type LiveFreeBoardDocumentBody = z.infer<typeof LiveFreeBoardDocumentBody>

export const LiveFreeBoardDocument = LiveFreeBoardDocumentBody.extend({
  id: LiveFreeBoardIdSchema,
})
export type LiveFreeBoardDocument = z.infer<typeof LiveFreeBoardDocument>

export class LiveFreeBoardDocuments {

  static readonly TextMinLength = 1
  static readonly TextMaxLength = 1000

  static toDocumentData(doc: LiveFreeBoardDocument): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): LiveFreeBoardDocument | null {
    if (!doc) {
      return null
    }

    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: LiveFreeBoardId(doc.id),
      kind: data["kind"],
      position: data["position"],
      text: data["text"],
      image: data["image"],
      visibility: data["visibility"],
      user: data["user"],
      last_updated_by_user_id: UserId(data["last_updated_by_user_id"]),
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

/**
 * フリーボードの新規作成リクエスト。
 *
 * LIVE に既存のフリーボードが存在しない場合のみ受理し、既存ありの場合は 409 を返す。
 * text / image のどちらか少なくとも一方は必須。
 *
 * 画像を含める場合は呼び出し前にクライアントが
 * `tmp/users/{firebaseUid}/spaces/{spaceKey}/lives/{liveId}/live-free-boards/image-uploaded`
 * に画像をアップロードしておく必要がある。
 */
export const CreateLiveFreeBoardRequest = z.object({
  position: LiveFreeBoardPosition,
  text: z.object({
    value: validateUserFreeInputText(
      { minLength: LiveFreeBoardDocuments.TextMinLength, maxLength: LiveFreeBoardDocuments.TextMaxLength },
    ),
  }).optional().describe("テキスト要素。省略するとテキストなしで作成される"),
  image: z.object({
    from_tmp: z.literal(true).describe("tmp パスにアップロード済みの画像を使うことを示すフラグ。必ず true"),
  }).optional().describe("画像要素。事前に tmp パスへ画像をアップロードする必要がある"),
}).refine(req => req.text !== undefined || req.image !== undefined, {
  message: "text と image のどちらか少なくとも一方は必須です",
})
export type CreateLiveFreeBoardRequest = z.infer<typeof CreateLiveFreeBoardRequest>

/**
 * フリーボードの sparse update リクエスト。
 *
 * - undefined (フィールド省略): 当該フィールドは無変更
 * - null: 当該フィールドを削除 (image の場合は Storage 画像も削除)
 * - 値: 当該フィールドを上書き (image を新しく差し替える場合は事前に tmp パスへ画像をアップロードする必要がある)
 *
 * 結果として `text` と `image` の両方が存在しなくなるような更新は 400 で弾く。
 */
export const UpdateLiveFreeBoardRequest = z.object({
  position: LiveFreeBoardPosition.optional().describe("位置。省略時は無変更"),
  text: z.object({
    value: validateUserFreeInputText(
      { minLength: LiveFreeBoardDocuments.TextMinLength, maxLength: LiveFreeBoardDocuments.TextMaxLength },
    ),
  }).nullable().optional().describe("テキスト要素。undefined: 無変更 / null: 削除 / 値: 上書き"),
  image: z.object({
    from_tmp: z.literal(true).describe("tmp パスにアップロード済みの画像を使うことを示すフラグ。必ず true"),
  }).nullable().optional().describe(
    "画像要素。undefined: 無変更 / null: 削除 (Storage 画像も削除) / 値: 新規 tmp アップロードで上書き",
  ),
})
export type UpdateLiveFreeBoardRequest = z.infer<typeof UpdateLiveFreeBoardRequest>

export const UpdateLiveFreeBoardPositionRequest = z.object({
  position: LiveFreeBoardPosition,
})
export type UpdateLiveFreeBoardPositionRequest = z.infer<typeof UpdateLiveFreeBoardPositionRequest>

export const UpdateLiveFreeBoardVisibilityRequest = z.object({
  visibility: LiveFreeBoardVisibility.describe("\"public\" で表示、\"hidden\" で非表示"),
})
export type UpdateLiveFreeBoardVisibilityRequest = z.infer<typeof UpdateLiveFreeBoardVisibilityRequest>
